import { computed, ref } from 'vue';
import { useMenuModuleStore } from '../stores/menuModuleStore';
import { useMenuLinkStore } from '../stores/menuLinkStore';
import type { MenuModuleModel, NavigationModuleWithLinks } from '../types/menuTypes';

export function useModuleApi() {
  const store = useMenuModuleStore();
  const linkStore = useMenuLinkStore();

  const sortedModules = computed(() => [...store.modules].sort((a, b) => a.order - b.order));

  const isModuleWithLinksLoading = ref(false);

  const modulesWithLinks = computed<NavigationModuleWithLinks[]>(() => {
    isModuleWithLinksLoading.value = true;
    const result = store.modules.map((module) => {
      const moduleLinks = linkStore.links.filter((link) => link.idModuleWard === module.id);

      const sortedLinks = [...moduleLinks].sort((a, b) => a.order - b.order);

      return {
        id: module.id,
        name: module.name,
        nameTranslations: module.nameTranslations,
        order: module.order,
        hidden: module.hidden,
        links: sortedLinks,
      };
    });

    const sortedResult = result.sort((a, b) => a.order - b.order);
    isModuleWithLinksLoading.value = false;
    return sortedResult;
  });

  const hasModules = computed(() => store.modules.length > 0);

  const isLoading = computed(() => store.loading);

  const hasError = computed(() => store.error !== null);

  async function updateModulesOrder(
    orderedModules: MenuModuleModel[],
    originalModules: MenuModuleModel[]
  ): Promise<void> {
    await store.updateModulesOrder(orderedModules, originalModules);
  }

  function getModuleById(id: number): MenuModuleModel | undefined {
    return store.modules.find((m) => m.id === id);
  }

  function moduleExists(id: number): boolean {
    return store.modules.some((m) => m.id === id);
  }

  function getModuleByLinkId(linkId: number): MenuModuleModel | undefined {
    const link = linkStore.links.find((l) => l.id === linkId);
    if (!link) return undefined;
    return store.modules.find((module) => module.id === link.idModuleWard);
  }

  return {
    // Store actions (delegate to store)
    fetchModules: store.fetchModules,
    createModule: store.createModule,
    updateModule: store.updateModule,
    deleteModule: store.deleteModule,
    updateModulesOrder,

    // Store state (reactive)
    modules: computed(() => store.modules),
    loading: isLoading,
    error: computed(() => store.error),

    // UI helpers
    sortedModules,
    modulesWithLinks,
    hasModules,
    hasError,
    getModuleById,
    moduleExists,
    getModuleByLinkId,
    isModuleWithLinksLoading,

    // State management (delegate to store)
    setModules: store.setModules,
    setModulesOrderArray: store.setModulesOrderArray,
    clearError: store.clearError,
  };
}
